import React, { useState } from 'react';

interface EmptyBucketConfirmModalProps {
  isOpen: boolean;
  bucketDisplayName: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const EmptyBucketConfirmModal: React.FC<EmptyBucketConfirmModalProps> = ({
  isOpen,
  bucketDisplayName,
  onConfirm,
  onCancel,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(bucketDisplayName).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (inputValue !== bucketDisplayName) {
      setError('Crate name does not match. Please type the exact name.');
      return;
    }
    setInputValue('');
    setError('');
    onConfirm();
  };

  const handleClose = () => {
    setInputValue('');
    setError('');
    onCancel();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '550px',
          width: '90%',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem', color: '#dc3545' }}>
          ⚠️ Empty Data Crate
        </h2>

        <div
          style={{
            padding: '1rem',
            backgroundColor: '#fff3cd',
            borderLeft: '4px solid #ffc107',
            borderRadius: '4px',
            marginBottom: '1.5rem',
          }}
        >
          <p style={{ margin: 0, marginBottom: '0.75rem', fontWeight: 600, color: '#856404' }}>
            Warning: This action cannot be undone!
          </p>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#856404', fontSize: '0.875rem' }}>
            <li>The entire crate and its contents will be deleted</li>
            <li>This action may take some time. Please do not close or refresh your browser</li>
            <li>You may need to run this command multiple times</li>
            <li>You may need to clear any conflicting object lock configurations before running this</li>
          </ul>
        </div>

        <p style={{ color: '#666', marginBottom: '1rem' }}>
          To confirm, please type the crate name:{' '}
          <strong style={{ color: '#333' }}>{bucketDisplayName}</strong>
          <button
            onClick={handleCopy}
            title="Copy crate name"
            style={{
              marginLeft: '6px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: copied ? '#28a745' : '#6c757d',
              padding: '2px 4px',
              verticalAlign: 'middle',
              borderRadius: '4px',
            }}
          >
            {copied ? '✓' : <CopyIcon />}
          </button>
        </p>

        <div className="form-group">
          <input
            type="text"
            className="form-input"
            placeholder={`Type "${bucketDisplayName}" to confirm`}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setError('');
            }}
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleConfirm();
              }
            }}
          />
        </div>

        {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}

        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={handleConfirm}
            disabled={!inputValue}
            className="btn"
            style={{
              flex: 1,
              backgroundColor: inputValue ? '#dc3545' : '#e0e0e0',
              color: 'white',
              cursor: inputValue ? 'pointer' : 'not-allowed',
            }}
          >
            Empty Crate
          </button>
          <button onClick={handleClose} className="btn btn-secondary" style={{ flex: 1 }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmptyBucketConfirmModal;
